import { motion, useReducedMotion } from 'framer-motion';
import { ReactNode, useLayoutEffect, useRef } from 'react';
import { getLogicalPath } from '@/lib/localeRouting';

const directions: Record<string, { x: number; y: number }> = {
  '/': { x: 0, y: 24 },
  '/services': { x: 32, y: 0 },
  '/projects': { x: -32, y: 0 },
  '/about': { x: 0, y: 32 },
  '/contact': { x: 0, y: -32 },
  '/blog': { x: 32, y: 12 },
};

const curtainEase = [0.76, 0, 0.24, 1] as const;
const contentEase = [0.22, 1, 0.36, 1] as const;

/** Full-screen cover shown while lazy route chunks load. Matches the route curtain. */
export function TransitionFallback() {
  return (
    <div
      aria-hidden
      className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center bg-[#0a0a0a]"
    >
      <span className="font-display text-2xl sm:text-3xl font-bold text-white/10 tracking-[-0.03em] select-none">
        figtures
      </span>
    </div>
  );
}

type RouteTransitionLayoutProps = {
  pathname: string;
  children: ReactNode;
};

/**
 * Route-level transition shell — mounts immediately on navigation (outside lazy Suspense)
 * so the curtain covers content before the next page chunk renders.
 */
export function RouteTransitionLayout({ pathname, children }: RouteTransitionLayoutProps) {
  const reduceMotion = useReducedMotion();
  const logicalPath = getLogicalPath(pathname);
  const dir = directions[logicalPath] || { x: 0, y: 24 };
  const isInitialRoute = useRef(true);
  const skipCurtain = isInitialRoute.current;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    isInitialRoute.current = false;
  }, [pathname]);

  if (reduceMotion) {
    return <div key={pathname}>{children}</div>;
  }

  return (
    <>
      {!skipCurtain ? (
        <>
          <motion.div
            key={`curtain-${pathname}`}
            aria-hidden
            className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center origin-top bg-[#0a0a0a]"
            initial={{ scaleY: 1 }}
            animate={{ scaleY: 0 }}
            transition={{ duration: 0.55, ease: curtainEase }}
          >
            <span className="font-display text-2xl sm:text-3xl font-bold text-white/10 tracking-[-0.03em] select-none">
              figtures
            </span>
          </motion.div>
          <motion.div
            key={`curtain-depth-${pathname}`}
            aria-hidden
            className="fixed inset-0 z-[99] pointer-events-none origin-bottom bg-[#111111]"
            initial={{ scaleY: 1 }}
            animate={{ scaleY: 0 }}
            transition={{ duration: 0.5, ease: curtainEase, delay: 0.04 }}
          />
        </>
      ) : null}

      <motion.div
        key={pathname}
        className="will-change-[opacity,transform]"
        initial={skipCurtain ? false : { opacity: 0, x: dir.x, y: dir.y }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{
          duration: 0.45,
          ease: contentEase,
          delay: skipCurtain ? 0 : 0.08,
        }}
      >
        {children}
      </motion.div>
    </>
  );
}

export default RouteTransitionLayout;
